import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Button,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  LocalShipping,
  Person,
  CalendarToday,
  CheckCircle,
  Refresh,
  Map as MapIcon,
  Visibility,
  Close,
  LocationOn,
  Phone,
  Email,
  Edit,
  Add,
  Lock,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Tooltip as LeafletTooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for different statuses
// Blue marker for pending/unassigned
const pendingIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const assignedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const completedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMTBiOTgxIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Truck icon for live driver locations
const truckIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj4KICA8Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxOCIgZmlsbD0iIzEwYjk4MSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIi8+CiAgPHBhdGggZmlsbD0id2hpdGUiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDgsIDgpIiBkPSJNMTggMTguNUMxOC44MyAxOC41IDE5LjUgMTcuODMgMTkuNSAxN1MxOC44MyAxNS41IDE4IDE1LjUgMTYuNSAxNi4xNyAxNi41IDE3IDE3LjE3IDE4LjUgMTggMTguNU0xOS41IDkuNUgxN1YxMkgyMS40NkwxOS41IDkuNU02IDE4LjVDNi44MyAxOC41IDcuNSAxNy44MyA3LjUgMTdTNi44MyAxNS41IDYgMTUuNSA0LjUgMTYuMTcgNC41IDE3IDUuMTcgMTguNSA2IDE4LjVNMjAgOEwyMyAxMlYxN0gyMUMyMSAxOC42NiAxOS42NiAyMCAxOCAyMFMxNSAxOC42NiAxNSAxN0g5QzkgMTguNjYgNy42NiAyMCA2IDIwUzMgMTguNjYgMyAxN0gxVjZDMSA0Ljg5IDEuODkgNCAzIDRIMTdWOEgyME0zIDZWMTVIMy43NkM0LjMxIDE0LjM5IDUuMTEgMTQgNiAxNFM3LjY5IDE0LjM5IDguMjQgMTVIMTVWNkgzWiIvPgo8L3N2Zz4=',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// Helper to create a coloured pin icon dynamically from any hex colour
const createColoredIcon = (color: string): L.Icon => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="${color}"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
  return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Helper function to get the icon based on status and driver (uses live driver list)
const getMarkerIcon = (event: CollectionEvent, driverList: any[]): L.Icon => {
  if (event.status === 'completed') return completedIcon;
  if (event.status === 'pending' || !event.driver_name) return pendingIcon;

  // Look up driver colour from the fetched drivers list
  const driverNameLower = (event.driver_name || event.current_driver_name || '').toLowerCase();
  const driver = driverList.find((d: any) => d.name.toLowerCase() === driverNameLower);
  if (driver && driver.color) {
    return createColoredIcon(driver.color);
  }

  // Default to blue for other assigned drivers
  return assignedIcon;
};

interface CollectionEvent {
  id: number;
  order_id: number;
  order_number: string;
  scheduled_date: string;
  status: 'pending' | 'assigned' | 'completed' | 'cancelled';
  assigned_driver_id?: number;
  driver_name?: string;
  current_driver_name?: string;
  driver_code?: string;
  completed_at?: string;
  completed_by?: number;
  notes?: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email?: string;
  loyalty_member?: number;
  previous_orders_count?: number;
  customer_phone?: string;
  delivery_address_1: string;
  delivery_postcode: string;
  delivery_latitude?: number;
  delivery_longitude?: number;
  service_type?: string;
  product_names?: string;
  bins_total_services?: number;
  bags_total_services?: number;
  large_bins_total_services?: number;
  beds_count?: number;
  mattress_count?: number;
  total?: string;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  collection_status?: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email?: string;
  customer_phone?: string;
  delivery_address_1: string;
  delivery_address_2?: string;
  delivery_city: string;
  delivery_postcode: string;
  delivery_country?: string;
  billing_address_1?: string;
  billing_address_2?: string;
  billing_city?: string;
  billing_postcode?: string;
  billing_country?: string;
  total: string;
  date_created?: string;
  completed_at?: string;
  customer_note?: string;
  product_names?: string;
  bins_per_occasion?: number;
  bins_number_of_occasions?: number;
  bags_per_occasion?: number;
  bags_number_of_occasions?: number;
  large_bins_per_occasion?: number;
  large_bins_number_of_occasions?: number;
  beds_count?: number;
  mattress_count?: number;
  bins_first_date?: number;
  bags_first_date?: number;
  large_bins_first_date?: number;
  first_date_bin_colours?: string;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
  total_collections?: number;
  completed_collections?: number;
  pending_collections?: number;
}

// Helper function to calculate marker offsets for overlapping locations
const getMarkerOffset = (events: CollectionEvent[], currentIndex: number): [number, number] => {
  const currentEvent = events[currentIndex];
  const lat = parseFloat(currentEvent.delivery_latitude?.toString() || '0');
  const lng = parseFloat(currentEvent.delivery_longitude?.toString() || '0');

  // Find all events at the same location
  const sameLocationIndices: number[] = [];
  events.forEach((event, idx) => {
    const eventLat = parseFloat(event.delivery_latitude?.toString() || '0');
    const eventLng = parseFloat(event.delivery_longitude?.toString() || '0');
    if (Math.abs(eventLat - lat) < 0.00001 && Math.abs(eventLng - lng) < 0.00001) {
      sameLocationIndices.push(idx);
    }
  });

  // If only one marker at this location, no offset needed
  if (sameLocationIndices.length <= 1) {
    return [lat, lng];
  }

  // Find the position of current event in the group
  const positionInGroup = sameLocationIndices.indexOf(currentIndex);

  // Calculate offset in a circular pattern (about 30 meters apart)
  const offsetAmount = 0.0003; // Approximately 30 meters
  const angle = (2 * Math.PI * positionInGroup) / sameLocationIndices.length;
  const offsetLat = lat + offsetAmount * Math.cos(angle);
  const offsetLng = lng + offsetAmount * Math.sin(angle);

  return [offsetLat, offsetLng];
};

const TodaysCollections: React.FC = () => {
  const [events, setEvents] = useState<CollectionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [driverLocations, setDriverLocations] = useState<any[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    unassigned: 0,
    completed: 0,
  });
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Order details modal state
  const [orderDetailsModal, setOrderDetailsModal] = useState<{ open: boolean; order: Order | null }>({ open: false, order: null });
  const [orderCollectionEvents, setOrderCollectionEvents] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Address and notes editing state
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editAddressData, setEditAddressData] = useState({
    delivery_address_1: '',
    delivery_address_2: '',
    delivery_city: '',
    delivery_postcode: '',
    delivery_country: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [adminNoteText, setAdminNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editNoteText, setEditNoteText] = useState('');

  // Bin colours editing state
  const [isEditingBinColours, setIsEditingBinColours] = useState(false);
  const [editBinColours, setEditBinColours] = useState({
    bin_brown: 0,
    bin_green: 0,
    bin_blue: 0,
    bin_black: 0,
    bin_grey: 0,
    bin_pink: 0,
  });
  const [savingBinColours, setSavingBinColours] = useState(false);

  // Cancel/Revert/Date dialogs state
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
  const [revertConfirmDialog, setRevertConfirmDialog] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
  const [dateDialog, setDateDialog] = useState<{ open: boolean; mode: 'add' | 'edit'; eventId: number | null; currentDate: string }>({
    open: false,
    mode: 'add',
    eventId: null,
    currentDate: ''
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => {
    fetchTodaysEvents(true); // Initial load with loading spinner
    fetchDrivers();
    fetchDriverLocations();

    // Refresh data without affecting scroll position
    const interval = setInterval(() => {
      fetchTodaysEvents(false);
      fetchDriverLocations();
    }, 60000); // Refresh every minute
    
    return () => {
      clearInterval(interval);
    };
  }, []);
  
  // Load map after component mounts and DOM is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        loadGoogleMaps();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (events.length > 0 && mapInstanceRef.current) {
      updateMapMarkers();
    }
  }, [events]);

  const loadGoogleMaps = () => {
    if (!(window as any).google) {
      const script = document.createElement('script');
      // Using a basic API key for development
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBqVgTlBp5x7nrWjP6TjQhKBYSNSEarPOk&callback=initMap`;
      script.async = true;
      script.defer = true;
      
      (window as any).initMap = () => {
        initializeMap();
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      
      document.head.appendChild(script);
    } else {
      initializeMap();
    }
  };

  const initializeMap = () => {
    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      if (mapRef.current && (window as any).google && (window as any).google.maps) {
        try {
          const mapOptions = {
            center: { lat: 53.4808, lng: -2.2426 }, // Manchester coordinates
            zoom: 10,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          };
          
          const map = new (window as any).google.maps.Map(mapRef.current, mapOptions);
          mapInstanceRef.current = map;
          
          // Update markers immediately if we have events
          if (events.length > 0) {
            updateMapMarkers();
          }
        } catch (error) {
          console.error('Error initializing map:', error);
        }
      }
    }, 100);
  };

  const updateMapMarkers = () => {
    if (!mapInstanceRef.current || !(window as any).google) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const bounds = new (window as any).google.maps.LatLngBounds();
    const infoWindow = new (window as any).google.maps.InfoWindow();

    events.forEach((event, index) => {
      if (event.delivery_latitude && event.delivery_longitude) {
        const position = {
          lat: parseFloat(event.delivery_latitude.toString()),
          lng: parseFloat(event.delivery_longitude.toString())
        };

        // Determine marker color based on status
        let markerColor = 'red'; // pending/unassigned
        if (event.status === 'completed') {
          markerColor = 'green';
        } else if (event.status === 'assigned') {
          markerColor = 'blue';
        }

        const marker = new (window as any).google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          title: `${event.order_number} - ${event.customer_first_name} ${event.customer_last_name}`,
          icon: {
            url: `http://maps.google.com/mapfiles/ms/icons/${markerColor}-dot.png`
          },
          label: {
            text: (index + 1).toString(),
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold'
          }
        });

        marker.addListener('click', () => {
          const content = `
            <div style="padding: 10px;">
              <strong>Order #${event.order_number}</strong><br/>
              <strong>${event.customer_first_name} ${event.customer_last_name}</strong><br/>
              ${event.delivery_address_1}<br/>
              ${event.delivery_postcode}<br/>
              ${event.customer_phone ? `<br/>📞 ${event.customer_phone}` : ''}
              <br/><br/>
              <strong>Service:</strong> ${getServiceLabel(event)}<br/>
              <strong>Driver:</strong> ${event.driver_name || 'Unassigned'}<br/>
              <strong>Status:</strong> ${event.status}
            </div>
          `;
          infoWindow.setContent(content);
          infoWindow.open(mapInstanceRef.current, marker);
        });

        markersRef.current.push(marker);
        bounds.extend(position);
      }
    });

    if (markersRef.current.length > 0) {
      mapInstanceRef.current.fitBounds(bounds);
      // Don't zoom in too much for a single marker
      if (markersRef.current.length === 1) {
        mapInstanceRef.current.setZoom(15);
      }
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/users', {
        params: { role: 'driver' }
      });
      setDrivers(response.data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchDriverLocations = async () => {
    try {
      const response = await api.get('/users/drivers/locations');
      setDriverLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching driver locations:', error);
    }
  };

  const handleDriverChange = async (eventId: number, newDriverId: number | null) => {
    try {
      // Update the driver assignment for this collection event
      await api.patch(`/collection-events/${eventId}/assign`, {
        driverId: newDriverId
      });

      // Refresh the events to show updated driver
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error updating driver assignment:', error);
    }
  };

  // Notification helper
  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({ open: true, message, severity });
  };

  // Order details modal handlers
  const handleViewOrderDetails = async (orderId: number) => {
    setLoadingDetails(true);
    setOrderDetailsModal({ open: true, order: null });
    setOrderCollectionEvents([]);
    setIsEditingAddress(false);
    setIsEditingNote(false);
    setIsEditingBinColours(false);
    setAdminNoteText('');

    try {
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/collection-events/order/${orderId}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showNotification('Failed to fetch order details', 'error');
      setOrderDetailsModal({ open: false, order: null });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsModal({ open: false, order: null });
    setIsEditingAddress(false);
    setIsEditingNote(false);
    setIsEditingBinColours(false);
    setAdminNoteText('');
  };

  const handleOverrideComplete = async (eventId: number) => {
    try {
      await api.put(`/collection-events/${eventId}/override-complete`);
      showNotification('Collection marked as completed', 'success');
      if (orderDetailsModal.order) {
        const [orderResponse, eventsResponse] = await Promise.all([
          api.get(`/orders/${orderDetailsModal.order.id}`),
          api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
        ]);
        setOrderDetailsModal({ open: true, order: orderResponse.data });
        setOrderCollectionEvents(eventsResponse.data);
      }
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error overriding completion:', error);
      showNotification('Failed to mark as completed', 'error');
    }
  };

  // Address editing handlers
  const handleStartEditAddress = () => {
    if (orderDetailsModal.order) {
      setEditAddressData({
        delivery_address_1: orderDetailsModal.order.delivery_address_1 || '',
        delivery_address_2: orderDetailsModal.order.delivery_address_2 || '',
        delivery_city: orderDetailsModal.order.delivery_city || '',
        delivery_postcode: orderDetailsModal.order.delivery_postcode || '',
        delivery_country: orderDetailsModal.order.delivery_country || ''
      });
      setIsEditingAddress(true);
    }
  };

  const handleCancelEditAddress = () => {
    setIsEditingAddress(false);
  };

  const handleSaveAddress = async () => {
    if (!orderDetailsModal.order) return;
    setSavingAddress(true);
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/address`, editAddressData);
      showNotification('Address updated successfully', 'success');
      setIsEditingAddress(false);
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error saving address:', error);
      showNotification('Failed to update address', 'error');
    } finally {
      setSavingAddress(false);
    }
  };

  // Notes editing handlers
  const handleStartEditNote = () => {
    if (orderDetailsModal.order) {
      setEditNoteText(orderDetailsModal.order.customer_note || '');
      setIsEditingNote(true);
    }
  };

  const handleCancelEditNote = () => {
    setIsEditingNote(false);
  };

  const handleSaveEditedNote = async () => {
    if (!orderDetailsModal.order) return;
    setSavingNote(true);
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/customer-note`, {
        customer_note: editNoteText
      });
      showNotification('Notes updated successfully', 'success');
      setIsEditingNote(false);
      const orderResponse = await api.get(`/orders/${orderDetailsModal.order.id}`);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      showNotification('Failed to update notes', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveAdminNote = async () => {
    if (!orderDetailsModal.order || !adminNoteText.trim()) return;
    setSavingNote(true);
    try {
      const existingNote = orderDetailsModal.order.customer_note || '';
      const timestamp = new Date().toLocaleString();
      const newNote = existingNote
        ? `${existingNote}\n\n--- Admin Note (${timestamp}) ---\n${adminNoteText}`
        : `--- Admin Note (${timestamp}) ---\n${adminNoteText}`;
      await api.patch(`/orders/${orderDetailsModal.order.id}/customer-note`, {
        customer_note: newNote
      });
      showNotification('Admin note added successfully', 'success');
      setAdminNoteText('');
      const orderResponse = await api.get(`/orders/${orderDetailsModal.order.id}`);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error saving admin note:', error);
      showNotification('Failed to add admin note', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  // Bin colours editing handlers
  const handleStartEditBinColours = () => {
    if (orderDetailsModal.order) {
      setEditBinColours({
        bin_brown: orderDetailsModal.order.bin_brown || 0,
        bin_green: orderDetailsModal.order.bin_green || 0,
        bin_blue: orderDetailsModal.order.bin_blue || 0,
        bin_black: orderDetailsModal.order.bin_black || 0,
        bin_grey: orderDetailsModal.order.bin_grey || 0,
        bin_pink: orderDetailsModal.order.bin_pink || 0,
      });
      setIsEditingBinColours(true);
    }
  };

  const handleCancelEditBinColours = () => {
    setIsEditingBinColours(false);
  };

  const handleSaveBinColours = async () => {
    if (!orderDetailsModal.order) return;

    setSavingBinColours(true);
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/bin-colours`, editBinColours);
      showNotification('Bin colours updated successfully', 'success');

      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
      setIsEditingBinColours(false);

      await fetchTodaysEvents(false);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update bin colours';
      showNotification(message, 'error');
    } finally {
      setSavingBinColours(false);
    }
  };

  // Date dialog handlers
  const handleOpenAddEventDialog = () => {
    setDateDialog({
      open: true,
      mode: 'add',
      eventId: null,
      currentDate: ''
    });
    setSelectedDate('');
  };

  const handleOpenEditDateDialog = (eventId: number, currentDate: string) => {
    setDateDialog({
      open: true,
      mode: 'edit',
      eventId,
      currentDate
    });
    setSelectedDate(currentDate.split('T')[0]);
  };

  const handleCloseDateDialog = () => {
    setDateDialog({
      open: false,
      mode: 'add',
      eventId: null,
      currentDate: ''
    });
    setSelectedDate('');
  };

  const handleSaveDate = async () => {
    if (!selectedDate) {
      showNotification('Please select a date', 'error');
      return;
    }

    if (!orderDetailsModal.order) {
      showNotification('No order selected', 'error');
      return;
    }

    // Prevent double-clicks
    if (savingDate) {
      return;
    }

    // Capture the current mode and eventId to prevent closure issues
    const currentMode = dateDialog.mode;
    const currentEventId = dateDialog.eventId;

    setSavingDate(true);
    try {
      if (currentMode === 'edit') {
        // Edit mode - must have a valid eventId
        if (!currentEventId) {
          console.error('Edit mode but no eventId provided');
          showNotification('Error: No event selected for editing', 'error');
          setSavingDate(false);
          return;
        }
        await api.patch(`/collection-events/${currentEventId}/date`, {
          scheduledDate: selectedDate
        });
        showNotification('Collection event date updated successfully', 'success');
      } else if (currentMode === 'add') {
        // Add mode - create new event
        await api.post('/collection-events', {
          orderId: orderDetailsModal.order.id,
          scheduledDate: selectedDate
        });
        showNotification('Collection event added successfully', 'success');
      } else {
        console.error('Unknown mode:', currentMode);
        showNotification('Error: Unknown operation mode', 'error');
        setSavingDate(false);
        return;
      }

      const eventsResponse = await api.get(`/collection-events/order/${orderDetailsModal.order.id}`);
      setOrderCollectionEvents(eventsResponse.data);
      handleCloseDateDialog();
      await fetchTodaysEvents(false);
    } catch (error: any) {
      console.error('Error saving collection event:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save collection event';
      showNotification(errorMessage, 'error');
    } finally {
      setSavingDate(false);
    }
  };

  // Override entire order complete
  const handleOverrideOrderComplete = async () => {
    if (!orderDetailsModal.order) return;

    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/override-complete`);
      showNotification('Order marked as completed', 'success');
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error marking order as complete:', error);
      showNotification('Failed to mark order as complete', 'error');
    }
  };

  // Revert order completion
  const handleRevertOrderComplete = async () => {
    if (!revertConfirmDialog.orderId) return;

    try {
      await api.patch(`/orders/${revertConfirmDialog.orderId}/revert-complete`);
      showNotification('Order reverted to assigned status', 'success');

      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${revertConfirmDialog.orderId}`),
        api.get(`/collection-events/order/${revertConfirmDialog.orderId}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
      await fetchTodaysEvents(false);
      setRevertConfirmDialog({ open: false, orderId: null });
    } catch (error) {
      console.error('Error reverting order:', error);
      showNotification('Failed to revert order status', 'error');
      setRevertConfirmDialog({ open: false, orderId: null });
    }
  };

  // Cancel order
  const handleCancelOrder = async () => {
    if (!cancelConfirmDialog.orderId) return;

    try {
      await api.post(`/orders/${cancelConfirmDialog.orderId}/cancel`);
      showNotification('Order and all collection events have been cancelled', 'success');
      setCancelConfirmDialog({ open: false, orderId: null });

      if (orderDetailsModal.order && orderDetailsModal.order.id === cancelConfirmDialog.orderId) {
        const [orderResponse, eventsResponse] = await Promise.all([
          api.get(`/orders/${cancelConfirmDialog.orderId}`),
          api.get(`/collection-events/order/${cancelConfirmDialog.orderId}`)
        ]);
        setOrderDetailsModal({ open: true, order: orderResponse.data });
        setOrderCollectionEvents(eventsResponse.data);
      }

      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error cancelling order:', error);
      showNotification('Failed to cancel order', 'error');
    }
  };

  // Helper function to get bin colours from order
  const getOrderBinColours = (order: Order): string[] => {
    const colours: string[] = [];
    if (order.bin_brown && order.bin_brown > 0) colours.push(`${order.bin_brown} Brown`);
    if (order.bin_green && order.bin_green > 0) colours.push(`${order.bin_green} Green`);
    if (order.bin_blue && order.bin_blue > 0) colours.push(`${order.bin_blue} Blue`);
    if (order.bin_black && order.bin_black > 0) colours.push(`${order.bin_black} Black`);
    if (order.bin_grey && order.bin_grey > 0) colours.push(`${order.bin_grey} Grey`);
    if (order.bin_pink && order.bin_pink > 0) colours.push(`${order.bin_pink} Pink`);
    // Append the free / one-off bin colour(s) (collected on the first date only)
    if (order.first_date_bin_colours) {
      try {
        const fd = JSON.parse(order.first_date_bin_colours);
        if (Array.isArray(fd)) colours.push(...fd);
      } catch (e) { /* ignore */ }
    }
    return colours;
  };

  const fetchTodaysEvents = async (isInitialLoad = false) => {
    try {
      // Only show loading spinner on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      
      // Fetch today's collection events
      const response = await api.get('/collection-events/today');
      const todaysEvents = response.data.events || [];
      
      setEvents(todaysEvents);
      
      // Calculate stats
      const assigned = todaysEvents.filter((e: CollectionEvent) => e.status === 'assigned').length;
      const completed = todaysEvents.filter((e: CollectionEvent) => e.status === 'completed').length;
      const pending = todaysEvents.filter((e: CollectionEvent) => e.status === 'pending').length;
      
      setStats({
        total: todaysEvents.length,
        assigned: assigned,
        unassigned: pending,
        completed: completed,
      });
    } catch (error) {
      console.error('Error fetching collection events:', error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const handleMarkComplete = async (event: CollectionEvent) => {
    try {
      await api.patch(`/collection-events/${event.id}/status`, {
        status: 'completed'
      });
      
      // Refresh the events
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error marking collection as complete:', error);
    }
  };

  const handleMarkIncomplete = async (event: CollectionEvent) => {
    try {
      await api.patch(`/collection-events/${event.id}/status`, {
        status: event.assigned_driver_id ? 'assigned' : 'pending'
      });
      
      // Refresh the events
      await fetchTodaysEvents(false);
    } catch (error) {
      console.error('Error marking collection as incomplete:', error);
    }
  };

  const handleBulkComplete = async () => {
    try {
      await api.patch('/collection-events/bulk/update', {
        eventIds: selectedEvents,
        action: 'status',
        value: 'completed'
      });
      
      setSelectedEvents([]);
      await fetchTodaysEvents();
    } catch (error) {
      console.error('Error in bulk complete:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'assigned':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getServiceLabel = (event: CollectionEvent) => {
    const parts = [];
    if (event.bins_total_services) parts.push(`${event.bins_total_services} ${event.bins_total_services === 1 ? 'Bin' : 'Bins'}`);
    if (event.bags_total_services) parts.push(`${event.bags_total_services} ${event.bags_total_services === 1 ? 'Bag' : 'Bags'}`);
    if (event.large_bins_total_services) parts.push(`${event.large_bins_total_services} Large ${event.large_bins_total_services === 1 ? 'Bin' : 'Bins'}`);
    if (event.beds_count) parts.push(`${event.beds_count} ${event.beds_count === 1 ? 'Bed' : 'Beds'}`);
    if (event.mattress_count) parts.push(`${event.mattress_count} ${event.mattress_count === 1 ? 'Mattress' : 'Mattresses'}`);
    return parts.join(', ') || event.product_names || 'Standard';
  };

  const getBinColours = (event: CollectionEvent): string[] => {
    const colours: string[] = [];
    
    // Check individual bin colour counts
    if (event.bin_brown && event.bin_brown > 0) colours.push(`${event.bin_brown} Brown`);
    if (event.bin_green && event.bin_green > 0) colours.push(`${event.bin_green} Green`);
    if (event.bin_blue && event.bin_blue > 0) colours.push(`${event.bin_blue} Blue`);
    if (event.bin_black && event.bin_black > 0) colours.push(`${event.bin_black} Black`);
    if (event.bin_grey && event.bin_grey > 0) colours.push(`${event.bin_grey} Grey`);
    if (event.bin_pink && event.bin_pink > 0) colours.push(`${event.bin_pink} Pink`);
    
    // If no individual counts but bin_colours field exists, try to parse it
    if (colours.length === 0 && event.bin_colours) {
      try {
        const parsedColours = JSON.parse(event.bin_colours);
        if (Array.isArray(parsedColours)) {
          return parsedColours;
        }
      } catch (e) {
        // If not JSON, return as is
        return [event.bin_colours];
      }
    }
    
    return colours;
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageEvents = events.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
      const newSelected = currentPageEvents
        .filter(e => e.status !== 'completed')
        .map(e => e.id);
      setSelectedEvents(newSelected);
    } else {
      setSelectedEvents([]);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectOne = (id: number) => {
    const selectedIndex = selectedEvents.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedEvents, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedEvents.slice(1));
    } else if (selectedIndex === selectedEvents.length - 1) {
      newSelected = newSelected.concat(selectedEvents.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedEvents.slice(0, selectedIndex),
        selectedEvents.slice(selectedIndex + 1),
      );
    }

    setSelectedEvents(newSelected);
  };

  const groupByDriver = () => {
    const grouped: { [key: string]: CollectionEvent[] } = {};
    events.forEach((event) => {
      const driver = event.driver_name || 'Unassigned';
      if (!grouped[driver]) {
        grouped[driver] = [];
      }
      grouped[driver].push(event);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const groupedEvents = groupByDriver();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            Today's Collections
          </Typography>
          <Button
            startIcon={<Refresh />}
            onClick={() => fetchTodaysEvents(false)}
            variant="contained"
            size="small"
            sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
          >
            Refresh
          </Button>
        </Box>

      {/* Top Row: Map on left, Stats on right */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Map - takes up 8 columns */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Collection's Map
            </Typography>
            <Box sx={{ height: 'calc(100% - 40px)', width: '100%', position: 'relative' }}>
              <MapContainer
                center={[53.4808, -2.2426]}
                zoom={11}
                style={{ height: '100%', width: '100%', borderRadius: '4px' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {/* Collection markers */}
                {events.map((event, index) => {
                  if (event.delivery_latitude && event.delivery_longitude) {
                    // Get the appropriate icon based on status and driver
                    const icon = getMarkerIcon(event, drivers);

                    // Get offset position for overlapping markers
                    const [offsetLat, offsetLng] = getMarkerOffset(events, index);

                    return (
                      <Marker
                        key={`collection-${event.id}`}
                        position={[offsetLat, offsetLng]}
                        icon={icon}
                      >
                        <Popup>
                          <div style={{ minWidth: '200px' }}>
                            <strong>Order #{event.order_number}</strong><br />
                            <strong>{event.customer_first_name} {event.customer_last_name}</strong><br />
                            {event.delivery_address_1}<br />
                            {event.delivery_postcode}<br />
                            {event.customer_phone && <><br />📞 {event.customer_phone}</>}
                            <br /><br />
                            <strong>Service:</strong> {getServiceLabel(event)}<br />
                            <strong>Driver:</strong> {event.driver_name || event.current_driver_name || 'Unassigned'}<br />
                            <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{event.status}</span>
                            <br /><br />
                            {event.status === 'completed' ? (
                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                <strong>Driver:</strong> {event.driver_name || event.current_driver_name || 'Unassigned'}
                              </Typography>
                            ) : (
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={event.assigned_driver_id || ''}
                                  onChange={(e) => handleDriverChange(event.id, e.target.value as number | null)}
                                  displayEmpty
                                  sx={{ fontSize: '12px' }}
                                >
                                  <MenuItem value=""><em>Unassigned</em></MenuItem>
                                  {drivers
                                    .filter(driver => driver.email !== 'system@historical.local' && driver.status === 'active')
                                    .map((driver) => (
                                      <MenuItem key={driver.id} value={driver.id}>
                                        {driver.name} {driver.driver_code ? `(${driver.driver_code})` : ''}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  }
                  return null;
                })}

                {/* Driver location markers */}
                {driverLocations.map((driver: any) => (
                  driver.latitude && driver.longitude ? (
                    <Marker
                      key={`driver-loc-${driver.id}`}
                      position={[parseFloat(driver.latitude), parseFloat(driver.longitude)]}
                      icon={truckIcon}
                    >
                      <LeafletTooltip permanent direction="top" offset={[0, -20]} opacity={0.9}>
                        <span style={{ fontWeight: 'bold', fontSize: '11px' }}>{driver.name}</span>
                      </LeafletTooltip>
                      <Popup>
                        <div style={{ minWidth: '160px' }}>
                          <strong>🚛 {driver.name}</strong><br />
                          <strong>Code:</strong> {driver.driver_code}<br />
                          <strong>Updated:</strong> {driver.minutes_since_update != null ? `${driver.minutes_since_update} min ago` : 'Unknown'}<br />
                          <strong>Active orders:</strong> {driver.active_orders}
                        </div>
                      </Popup>
                    </Marker>
                  ) : null
                ))}
              </MapContainer>
              {/* Map Legend */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 1,
                  p: 1,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  fontSize: '11px',
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  Driver Key
                </Typography>
                {drivers
                  .filter((d: any) => d.email !== 'system@historical.local' && d.status === 'active')
                  .map((driver: any) => (
                    <Box key={driver.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.3, gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: driver.color || '#2563EB', borderRadius: '50%' }} />
                      <span>{driver.name}</span>
                    </Box>
                  ))}
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mt: 0.5, mb: 0.3 }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#2563eb', borderRadius: '50%', mr: 0.5 }} />
                  <span>Pending</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#10b981', borderRadius: '50%', mr: 0.5 }} />
                  <span>Completed</span>
                </Box>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mt: 0.5, mb: 0.3 }}>
                  Live
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 14, height: 14, backgroundColor: '#10b981', borderRadius: '50%', mr: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '8px', color: 'white' }}>🚛</span>
                  </Box>
                  <span>Driver location</span>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* Stats Cards - takes up 4 columns in 2x2 grid */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <CalendarToday color="primary" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.total}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Person color="info" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.assigned}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Assigned
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <LocalShipping color="warning" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.unassigned}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ height: '190px' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <CheckCircle color="success" sx={{ fontSize: 35, mb: 1 }} />
                  <Typography variant="h3">
                    {stats.completed}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Table Section - Full Width */}
      <Paper sx={{ p: 2, mt: 3, maxHeight: '600px', overflow: 'auto' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Collection List</Typography>
              {selectedEvents.length > 0 && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleBulkComplete}
                >
                  Complete Selected ({selectedEvents.length})
                </Button>
              )}
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedEvents.length > 0 && selectedEvents.length < events.filter(e => e.status !== 'completed').length}
                        checked={events.filter(e => e.status !== 'completed').length > 0 && selectedEvents.length === events.filter(e => e.status !== 'completed').length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Colours</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((event, index) => (
                    <TableRow 
                      key={event.id}
                      sx={{ 
                        bgcolor: event.status === 'completed' ? 'action.hover' : 'inherit',
                        opacity: event.status === 'completed' ? 0.7 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          disabled={event.status === 'completed'}
                          checked={selectedEvents.indexOf(event.id) !== -1}
                          onChange={() => handleSelectOne(event.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          #{event.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.customer_first_name} {event.customer_last_name}
                          {(event.loyalty_member ?? 0) > 0 && (event.previous_orders_count ?? 0) > 0 && (
                            <Tooltip title="These are customers with accounts and are part of the loyalty scheme">
                              <Box
                                component="span"
                                sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1, cursor: 'help' }}
                              >
                                *
                              </Box>
                            </Tooltip>
                          )}
                        </Typography>
                        {event.customer_phone && (
                          <Typography variant="caption" color="text.secondary">
                            {event.customer_phone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.delivery_address_1}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {event.delivery_postcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {event.status === 'completed' ? (
                          <Typography variant="body2">
                            {event.driver_name || event.current_driver_name || 'Unassigned'}
                          </Typography>
                        ) : (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={event.assigned_driver_id || ''}
                              onChange={(e) => handleDriverChange(event.id, e.target.value as number | null)}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Unassigned</em>
                              </MenuItem>
                              {drivers
                                .filter(driver =>
                                  driver.email !== 'system@historical.local' && driver.status === 'active'
                                )
                                .map((driver) => (
                                  <MenuItem key={driver.id} value={driver.id}>
                                    {driver.name} {driver.driver_code ? `(${driver.driver_code})` : ''}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {getServiceLabel(event)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {getBinColours(event).join(', ') || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={event.status.toUpperCase()}
                          color={getStatusColor(event.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Order Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrderDetails(event.order_id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={events.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
      </Paper>

      {/* Driver Groups Summary */}
      {Object.keys(groupedEvents).length > 0 && (
        <>
          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            Driver Collection Summary
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(groupedEvents).map(([driver, driverEvents]) => (
          <Grid item xs={12} md={4} key={driver}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {driver}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {driverEvents.length} collections
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Completed: {driverEvents.filter(e => e.status === 'completed').length} / {driverEvents.length}
                </Typography>
              </Box>
            </Paper>
            </Grid>
          ))}
          </Grid>
        </>
      )}

      {/* Order Details Modal */}
      <Dialog
        open={orderDetailsModal.open}
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Order Details #{orderDetailsModal.order?.order_number}
            </Typography>
            <IconButton onClick={handleCloseOrderDetails} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>Loading order details...</Typography>
            </Box>
          ) : orderDetailsModal.order ? (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Information
                </Typography>
                <Box mb={2}>
                  <Typography variant="body1" fontWeight="medium">
                    {orderDetailsModal.order.customer_first_name} {orderDetailsModal.order.customer_last_name}
                  </Typography>
                  {orderDetailsModal.order.customer_email && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{orderDetailsModal.order.customer_email}</Typography>
                    </Box>
                  )}
                  {orderDetailsModal.order.customer_phone && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{orderDetailsModal.order.customer_phone}</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Collection Address */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Collection Address
                  </Typography>
                  {!isEditingAddress && orderDetailsModal.order.status !== 'cancelled' && (
                    <IconButton size="small" onClick={handleStartEditAddress} title="Edit Address">
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {isEditingAddress ? (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 1"
                      value={editAddressData.delivery_address_1}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_address_1: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 2"
                      value={editAddressData.delivery_address_2}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_address_2: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={editAddressData.delivery_city}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_city: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Postcode"
                      value={editAddressData.delivery_postcode}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_postcode: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      value={editAddressData.delivery_country}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_country: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <Box display="flex" gap={1} mt={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveAddress}
                        disabled={savingAddress}
                      >
                        {savingAddress ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleCancelEditAddress}>
                        Cancel
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Changes will apply to all pending collections for this order.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Note: Map location only updates if the postcode is changed.
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2">{orderDetailsModal.order.delivery_address_1}</Typography>
                      {orderDetailsModal.order.delivery_address_2 && (
                        <Typography variant="body2">{orderDetailsModal.order.delivery_address_2}</Typography>
                      )}
                      <Typography variant="body2">
                        {orderDetailsModal.order.delivery_city}, {orderDetailsModal.order.delivery_postcode}
                      </Typography>
                      {orderDetailsModal.order.delivery_country && (
                        <Typography variant="body2">{orderDetailsModal.order.delivery_country}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Billing Address
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2">{orderDetailsModal.order.billing_address_1}</Typography>
                    {orderDetailsModal.order.billing_address_2 && (
                      <Typography variant="body2">{orderDetailsModal.order.billing_address_2}</Typography>
                    )}
                    <Typography variant="body2">
                      {orderDetailsModal.order.billing_city}, {orderDetailsModal.order.billing_postcode}
                    </Typography>
                    {orderDetailsModal.order.billing_country && (
                      <Typography variant="body2">{orderDetailsModal.order.billing_country}</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order Information
                </Typography>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Order Date:</strong> {orderDetailsModal.order.date_created ? new Date(orderDetailsModal.order.date_created).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Collection Status:</strong>{' '}
                    {(() => {
                      if (orderDetailsModal.order.status === 'cancelled' || orderDetailsModal.order.collection_status === 'cancelled') {
                        return <Chip label="CANCELLED" color="error" size="small" />;
                      }
                      if (orderDetailsModal.order.collection_status === 'collection_complete') {
                        return <Chip label="COMPLETED" color="success" size="small" />;
                      }
                      const total = orderDetailsModal.order.total_collections || orderCollectionEvents.length || 0;
                      const completed = orderDetailsModal.order.completed_collections || orderCollectionEvents.filter(e => e.status === 'completed').length || 0;
                      const pending = orderDetailsModal.order.pending_collections || orderCollectionEvents.filter(e => e.status !== 'completed' && e.status !== 'cancelled').length || 0;
                      if (total === 0) {
                        return <Chip label="NO COLLECTIONS" color="default" size="small" />;
                      }
                      if (completed === total) {
                        return <Chip label="COMPLETED" color="success" size="small" />;
                      }
                      if (pending > 0) {
                        return <Chip label={`${pending} COLLECTION${pending > 1 ? 'S' : ''} LEFT`} color="warning" size="small" />;
                      }
                      return <Chip label="OVERDUE" color="error" size="small" />;
                    })()}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong> £{parseFloat(orderDetailsModal.order.total).toFixed(2)}
                  </Typography>
                </Box>
                {(() => {
                  const currentEvent = orderCollectionEvents.find(e =>
                    e.status === 'assigned' || e.status === 'pending'
                  ) || orderCollectionEvents[0];
                  const currentDriver = currentEvent?.driver_name || currentEvent?.current_driver_name;

                  if (currentDriver) {
                    return (
                      <Box mb={1}>
                        <Typography variant="body2">
                          <strong>Current Driver:</strong> {currentDriver}
                        </Typography>
                        {currentEvent?.assigned_at && (
                          <Typography variant="body2">
                            <strong>Assigned At:</strong> {new Date(currentEvent.assigned_at).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })()}
                {orderDetailsModal.order.completed_at && (
                  <Box mb={1}>
                    <Typography variant="body2">
                      <strong>Completed At:</strong> {new Date(orderDetailsModal.order.completed_at).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Collection Events */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Collection Events
                  </Typography>
                  {orderDetailsModal.order.status !== 'cancelled' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<Add />}
                      onClick={handleOpenAddEventDialog}
                    >
                      Add Event
                    </Button>
                  )}
                </Box>
                {orderCollectionEvents.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Driver</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderCollectionEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              {new Date(event.scheduled_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={event.status.toUpperCase()}
                                size="small"
                                color={
                                  event.status === 'completed' ? 'success' :
                                  event.status === 'assigned' ? 'info' :
                                  event.status === 'pending' ? 'warning' : 'default'
                                }
                              />
                              {event.override_completed === 1 && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  (Override)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {event.current_driver_name || event.driver_name || '-'}
                            </TableCell>
                            <TableCell>
                              {event.status !== 'completed' ? (
                                <Box display="flex" gap={1}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenEditDateDialog(event.id, event.scheduled_date)}
                                    title="Edit Date"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleOverrideComplete(event.id)}
                                  >
                                    Override Complete
                                  </Button>
                                </Box>
                              ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Lock fontSize="small" color="disabled" />
                                  <Typography variant="body2" color="text.secondary">
                                    Completed
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No collection events found
                  </Typography>
                )}
              </Grid>

              {/* Products Ordered */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Products Ordered
                </Typography>
                <Typography variant="body2">
                  {orderDetailsModal.order.product_names || 'No product information available'}
                </Typography>

                {/* Service Details */}
                {(((orderDetailsModal.order.bins_per_occasion ?? 0) > 0) ||
                  ((orderDetailsModal.order.bags_per_occasion ?? 0) > 0) ||
                  ((orderDetailsModal.order.large_bins_per_occasion ?? 0) > 0) ||
                  ((orderDetailsModal.order.bins_first_date ?? 0) > 0) ||
                  ((orderDetailsModal.order.bags_first_date ?? 0) > 0) ||
                  ((orderDetailsModal.order.large_bins_first_date ?? 0) > 0)) && (
                  <Box mt={2}>
                    {(orderDetailsModal.order.bins_per_occasion ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Bins:</strong> {orderDetailsModal.order.bins_per_occasion} per occasion × {orderDetailsModal.order.bins_number_of_occasions || 1} {(orderDetailsModal.order.bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.bags_per_occasion ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Bags:</strong> {orderDetailsModal.order.bags_per_occasion} per occasion × {orderDetailsModal.order.bags_number_of_occasions || 1} {(orderDetailsModal.order.bags_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.large_bins_per_occasion ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Large Bins:</strong> {orderDetailsModal.order.large_bins_per_occasion} per occasion × {orderDetailsModal.order.large_bins_number_of_occasions || 1} {(orderDetailsModal.order.large_bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.bins_first_date ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Free bin (first collection date only):</strong> {orderDetailsModal.order.bins_first_date}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.bags_first_date ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Free bags (first collection date only):</strong> {orderDetailsModal.order.bags_first_date}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.large_bins_first_date ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Free large bin (first collection date only):</strong> {orderDetailsModal.order.large_bins_first_date}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Beds & Mattresses */}
                {((orderDetailsModal.order.beds_count ?? 0) > 0 || (orderDetailsModal.order.mattress_count ?? 0) > 0) && (
                  <Box mt={1}>
                    {(orderDetailsModal.order.beds_count ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Beds:</strong> {orderDetailsModal.order.beds_count}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.mattress_count ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Mattresses:</strong> {orderDetailsModal.order.mattress_count}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Bin Colours */}
                {((orderDetailsModal.order.bins_per_occasion ?? 0) > 0 || (orderDetailsModal.order.bins_first_date ?? 0) > 0 || getOrderBinColours(orderDetailsModal.order).length > 0) && (
                  <Box mt={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        <strong>Bin Colours:</strong>{' '}
                        {!isEditingBinColours && (getOrderBinColours(orderDetailsModal.order).join(', ') || 'None set')}
                      </Typography>
                      {!isEditingBinColours && orderDetailsModal.order.status !== 'cancelled' && (
                        <IconButton size="small" onClick={handleStartEditBinColours} title="Edit Bin Colours">
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {isEditingBinColours && (
                      <Box mt={1}>
                        <Grid container spacing={1}>
                          {[
                            { key: 'bin_brown', label: 'Brown' },
                            { key: 'bin_green', label: 'Green' },
                            { key: 'bin_blue', label: 'Blue' },
                            { key: 'bin_black', label: 'Black' },
                            { key: 'bin_grey', label: 'Grey' },
                            { key: 'bin_pink', label: 'Pink' },
                          ].map(({ key, label }) => (
                            <Grid item xs={4} key={key}>
                              <TextField
                                label={label}
                                type="number"
                                size="small"
                                fullWidth
                                value={editBinColours[key as keyof typeof editBinColours]}
                                onChange={(e) => setEditBinColours({
                                  ...editBinColours,
                                  [key]: parseInt(e.target.value) || 0
                                })}
                                inputProps={{ min: 0 }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Box display="flex" gap={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveBinColours}
                            disabled={savingBinColours}
                          >
                            {savingBinColours ? 'Saving...' : 'Save'}
                          </Button>
                          <Button size="small" variant="outlined" onClick={handleCancelEditBinColours}>
                            Cancel
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Changes will apply to all pending collections for this order.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Customer Notes */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Delivery Notes
                  </Typography>
                  {!isEditingNote && orderDetailsModal.order.status !== 'cancelled' && (
                    <IconButton size="small" onClick={handleStartEditNote} title="Edit Notes">
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {isEditingNote ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      value={editNoteText}
                      onChange={(e) => setEditNoteText(e.target.value)}
                      placeholder="Enter delivery notes..."
                      sx={{ mb: 1 }}
                    />
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveEditedNote}
                        disabled={savingNote}
                      >
                        {savingNote ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleCancelEditNote}>
                        Cancel
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Changes will apply to all pending collections for this order.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {orderDetailsModal.order.customer_note || 'No delivery notes'}
                      </Typography>
                    </Paper>

                    {/* Add Admin Note */}
                    {orderDetailsModal.order.status !== 'cancelled' && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Add Admin Note
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={adminNoteText}
                          onChange={(e) => setAdminNoteText(e.target.value)}
                          placeholder="Add a note for the driver..."
                          sx={{ mb: 1 }}
                        />
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveAdminNote}
                            disabled={savingNote || !adminNoteText.trim()}
                          >
                            {savingNote ? 'Saving...' : 'Add Note'}
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Note will be appended and shown to drivers on all pending collections.
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          ) : (
            <Typography>No order details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {orderDetailsModal.order && orderDetailsModal.order.status !== 'cancelled' && orderDetailsModal.order.collection_status !== 'collection_complete' && (
            <>
              <Button
                onClick={() => setCancelConfirmDialog({ open: true, orderId: orderDetailsModal.order!.id })}
                color="error"
                variant="outlined"
                sx={{ mr: 'auto' }}
              >
                Cancel Order
              </Button>
              <Button
                onClick={handleOverrideOrderComplete}
                color="primary"
                variant="contained"
                sx={{ mr: 1 }}
              >
                Override Order Complete
              </Button>
            </>
          )}
          {orderDetailsModal.order && orderDetailsModal.order.status !== 'cancelled' && orderDetailsModal.order.collection_status === 'collection_complete' && (
            <Button
              onClick={() => setRevertConfirmDialog({ open: true, orderId: orderDetailsModal.order!.id })}
              color="warning"
              variant="outlined"
              sx={{ mr: 'auto' }}
            >
              Revert to Assigned
            </Button>
          )}
          <Button onClick={handleCloseOrderDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={cancelConfirmDialog.open}
        onClose={() => setCancelConfirmDialog({ open: false, orderId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="error">
            Confirm Order Cancellation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to cancel this order?
          </Alert>
          <Typography variant="body2" paragraph>
            This action will:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2">Cancel all pending collection events</Typography>
            <Typography component="li" variant="body2">Remove them from today's collections</Typography>
            <Typography component="li" variant="body2">Remove them from upcoming collections</Typography>
            <Typography component="li" variant="body2" color="error" fontWeight="bold">This cannot be undone</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmDialog({ open: false, orderId: null })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCancelOrder} color="error" variant="contained">
            Yes, Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert Order Completion Confirmation Dialog */}
      <Dialog
        open={revertConfirmDialog.open}
        onClose={() => setRevertConfirmDialog({ open: false, orderId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="warning.main">
            Confirm Order Revert
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to revert this completed order back to assigned status?
          </Alert>
          <Typography variant="body2" paragraph>
            This action will:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2">Change order status from "Complete" to "Assigned"</Typography>
            <Typography component="li" variant="body2">Reset collection events to "Assigned" status</Typography>
            <Typography component="li" variant="body2">Clear all completion timestamps and data</Typography>
            <Typography component="li" variant="body2">Remove completion overrides</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertConfirmDialog({ open: false, orderId: null })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRevertOrderComplete} color="warning" variant="contained">
            Yes, Revert Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date Picker Dialog for Add/Edit Collection Event */}
      <Dialog
        open={dateDialog.open}
        onClose={handleCloseDateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dateDialog.mode === 'add' ? 'Add Collection Event' : 'Edit Collection Date'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Collection Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
            />
            {dateDialog.mode === 'edit' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Current date: {new Date(dateDialog.currentDate).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDateDialog} color="primary" disabled={savingDate}>
            Cancel
          </Button>
          <Button onClick={handleSaveDate} color="primary" variant="contained" disabled={savingDate}>
            {savingDate ? 'Saving...' : (dateDialog.mode === 'add' ? 'Add Event' : 'Update Date')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TodaysCollections;